import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import Button from '../../components/common/Button';
import FileUpload from '../../components/common/FileUpload';
import { FileViewer } from '../common/FileViewer';
import { AccountsPackItemDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import { useFileApi } from '../../hooks/useFileApi';
import { View } from 'lucide-react';

interface AccountsPackItemFormProps {
  initialValues: AccountsPackItemDTO;
  onSubmit: (data: AccountsPackItemDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information'
  }
} as const;

const AccountsPackItemForm: React.FC<AccountsPackItemFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<AccountsPackItemDTO>(initialValues);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<{
    url: string;
    fileName: string;
  } | null>(null);
  const { uploadFile, getFileUrl } = useFileApi();

  
            const validationRules: ValidationRules = {
                    emailSubject: [
      { required: true, message: 'Email Subject is required' }
    ],
    emailFrom: [
      { required: true, message: 'Email From is required' }
    ],
    periodYear: [
      { required: true, message: 'Period Year is required' },
      { custom: (value) => Number(value) > 0, message: 'Period Year must be a valid year' }
    ],
    periodMonth: [
      { required: true, message: 'Period Month is required' },
      { custom: (value) => Number(value) >= 1 && Number(value) <= 12, message: 'Period Month must be between 1 and 12' }
    ]
            };

  const { errors, validate } = useFormValidation<AccountsPackItemDTO>(validationRules);

  // Load existing file preview
  useEffect(() => {
    const loadFilePreview = async () => {
      if (initialValues.fileRecordId) {
        try {
          const response = await getFileUrl(initialValues.fileRecordId);
          if (response && response.data && response.success) {
            setThumbnailUrl(response.data.url || null);
          }
        } catch (error) {
          console.error('Error loading file preview:', error);
        }
      }
    };
    loadFilePreview();
  }, [initialValues.fileRecordId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      // Create a preview URL for images
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setThumbnailUrl(url);
      }
    }
  };

  const handleViewFile = useCallback(async () => {
    if (!formData.fileRecordId) {
      console.error('No file record ID available');
      return;
    }

    try {
      const response = await getFileUrl(formData.fileRecordId);
      if (response && response.data && response.success) {
        setSelectedFile({
          url: response.data.url || '',
          fileName: response.data.fileName || 'File',
        });
        setViewerOpen(true);
      }
    } catch (error) {
      console.error('Error getting file URL:', error);
    }
  }, [formData.fileRecordId, getFileUrl]);

  const handleCloseViewer = useCallback(() => {
    setViewerOpen(false);
    setSelectedFile(null);
  }, []);

  const viewerProps = useMemo(() => {
    if (!viewerOpen || !selectedFile) return null;
    return {
      fileName: selectedFile.fileName,
      sasUrl: selectedFile.url,
      onClose: handleCloseViewer,
    };
  }, [viewerOpen, selectedFile, handleCloseViewer]);

  const handleSave = async () => {
    if (validate(formData)) {
      let finalData = { ...formData };
      
      // Upload file if a new one was selected
      if (uploadedFile) {
        try {
          const uploadResponse = await uploadFile(
            'accounts-pack-items',
            uploadedFile.name,
            uploadedFile
          );
          if (uploadResponse && uploadResponse.data) {
            finalData.fileRecordId = uploadResponse.data;
          }
        } catch (error) {
          console.error('Error uploading file:', error);
          return; // Don't proceed if file upload fails
        }
      }
      
      onSubmit(finalData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  // Generate years: current year ± 5 years
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 11 }, (_, i) => {
    const year = currentYear - 5 + i;
    return { value: year.toString(), label: year.toString() };
  });

  // Month options with friendly names
  const monthOptions = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  // Helper to check if file is an image
  const isImageFile = (filename?: string): boolean => {
    if (!filename) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageExtensions.some(ext => filename.toLowerCase().endsWith(ext));
  };

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel} isSubmitting={isLoading}>
      <FormSection 
        id={FORM_SECTIONS.basicInfo.id} 
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="emailSubject"
          label="Email Subject"
          type="text"
          value={formData.emailSubject || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Email Subject"
          required={true}
          error={errors.emailSubject}
        />
        <FormField
          id="emailFrom"
          label="Email From"
          type="text"
          value={formData.emailFrom || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Email From"
          required={true}
          error={errors.emailFrom}
        />
        <FormField
          id="emailMessageId"
          label="Email Message Id"
          type="text"
          value={formData.emailMessageId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Email Message Id"
          required={false}
          error={errors.emailMessageId}
        />
        <FormField
          id="periodYear"
          label="Period Year"
          type="dropdown"
          value={formData.periodYear?.toString() || ''}
          onChange={(e) => {
            const value = e.target.value ? parseInt(e.target.value) : undefined;
            setFormData(prev => ({ ...prev, periodYear: value }));
          }}
          options={yearOptions}
          required={true}
          error={errors.periodYear}
        />
        <FormField
          id="periodMonth"
          label="Period Month"
          type="dropdown"
          value={formData.periodMonth?.toString() || ''}
          onChange={(e) => {
            const value = e.target.value ? parseInt(e.target.value) : undefined;
            setFormData(prev => ({ ...prev, periodMonth: value }));
          }}
          options={monthOptions}
          required={true}
          error={errors.periodMonth}
        />
        <FormField
          id="notes"
          label="Notes"
          type="textarea"
          value={formData.notes || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter any notes about this accounts pack item"
          required={false}
          error={errors.notes}
        />

        {/* File Preview Section */}
        {(formData.fileRecordId || thumbnailUrl) && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Current File
            </label>
            <div className="flex items-center gap-4 p-4 border border-gray-300 dark:border-gray-600 rounded">
              {thumbnailUrl && isImageFile(formData.fileRecord?.fileName) ? (
                <img 
                  src={thumbnailUrl} 
                  alt={formData.fileRecord?.fileName || 'Preview'}
                  className="h-20 w-20 object-cover rounded"
                />
              ) : thumbnailUrl ? (
                <div className="h-20 w-20 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded">
                  <svg className="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                  </svg>
                </div>
              ) : null}
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {formData.fileRecord?.fileName || 'File attached'}
                </p>
                {formData.fileRecord?.finalSizeInBytes && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {(formData.fileRecord.finalSizeInBytes / 1024 / 1024).toFixed(2)} MB
                  </p>
                )}
              </div>
              {formData.fileRecordId && (
                <Button
                  type="button"
                  onClick={handleViewFile}
                  variant="primary"
                  icon={View}
                  iconPosition="left"
                >
                  View
                </Button>
              )}
            </div>
          </div>
        )}

        {/* File Upload */}
        <FileUpload
          id="file"
          onChange={handleFileChange}
          title={formData.fileRecordId ? "Replace file" : "Upload file"}
          accept={['.pdf', '.jpg', '.jpeg', '.png', '.gif']}
          required={false}
        />

      </FormSection>

      {/* File Viewer */}
      {viewerProps && <FileViewer {...viewerProps} />}
    </Form>
  );
};

export default AccountsPackItemForm;